import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Search, Target } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import type { AITopMatchesBrief as Brief, AITopMatchesStats } from "@/hooks/jobs/useAITopMatches";

const CEFR = ["", "A1", "A2", "B1", "B2", "C1", "C2", "native"];

interface AITopMatchesBriefProps {
	brief: Brief;
	stats?: AITopMatchesStats;
	defaultOpen?: boolean;
}

export function AITopMatchesBrief({ brief, stats, defaultOpen = false }: AITopMatchesBriefProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(defaultOpen);
	const trace = (stats?.scout_trace ?? []).filter((entry) => entry.tool === "search_profiles");
	const years = brief.seniority.min_years !== null || brief.seniority.max_years !== null
		? ` · ${brief.seniority.min_years ?? "?"}–${brief.seniority.max_years ?? "?"} ${t("aiTopMatches.brief.years")}`
		: "";

	return (
		<Collapsible open={open} onOpenChange={setOpen} className="rounded-sm border border-border bg-muted/30">
			<CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium">
				{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
				<Target className="h-4 w-4 text-purple-600" />
				<span>{t("aiTopMatches.brief.title")}</span>
				{!open && brief.role_summary && (
					<span className="ml-2 truncate font-normal text-muted-foreground">{brief.role_summary}</span>
				)}
			</CollapsibleTrigger>
			<CollapsibleContent className="space-y-4 border-t border-border px-3 py-3 text-sm">
				{brief.role_summary && <p className="text-muted-foreground">{brief.role_summary}</p>}

				{brief.must_haves.length > 0 && (
					<div>
						<div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("aiTopMatches.brief.mustHaves")}</div>
						<div className="space-y-1">
							{brief.must_haves.map((c) => (
								<div key={c.id} className="flex flex-wrap items-center gap-1.5">
									<Badge variant="outline" className="rounded-sm border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
										{c.id} · {c.label}
									</Badge>
									<span className="text-xs text-muted-foreground">{"★".repeat(c.weight)}</span>
									<span className="text-xs text-muted-foreground">{c.evidence_terms.join(", ")}</span>
								</div>
							))}
						</div>
					</div>
				)}

				{brief.nice_to_haves.length > 0 && (
					<div>
						<div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("aiTopMatches.brief.niceToHaves")}</div>
						<div className="flex flex-wrap gap-1.5">
							{brief.nice_to_haves.map((c) => (
								<Badge key={c.id} variant="outline" className="rounded-sm border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300">
									{c.id} · {c.label}
								</Badge>
							))}
						</div>
					</div>
				)}

				{brief.deal_breakers.length > 0 && (
					<div>
						<div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("aiTopMatches.brief.dealBreakers")}</div>
						<div className="flex flex-wrap gap-1.5">
							{brief.deal_breakers.map((item) => (
								<Badge key={item} variant="outline" className="rounded-sm border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">{item}</Badge>
							))}
						</div>
					</div>
				)}

				<div className="grid gap-3 sm:grid-cols-3">
					<div>
						<div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("aiTopMatches.brief.seniority")}</div>
						<div>{brief.seniority.level}{years}</div>
					</div>
					<div>
						<div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("aiTopMatches.brief.languages")}</div>
						<div>
							{brief.languages.length === 0
								? "—"
								: brief.languages.map((l) => `${l.code.toUpperCase()}${CEFR[l.min_cefr] ? ` ≥ ${CEFR[l.min_cefr]}` : ""}${l.mandatory ? " *" : ""}`).join(", ")}
						</div>
					</div>
					<div>
						<div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("aiTopMatches.brief.location")}</div>
						<div>{brief.location.mode}{brief.location.place ? ` · ${brief.location.place}` : ""}</div>
					</div>
				</div>

				{brief.title_synonyms.length > 0 && (
					<div>
						<div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("aiTopMatches.brief.titles")}</div>
						<div className="text-muted-foreground">{brief.title_synonyms.join(", ")}</div>
					</div>
				)}

				{trace.length > 0 && (
					<div>
						<div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
							<Search className="h-3.5 w-3.5" />
							{t("aiTopMatches.brief.searchTrace")}
						</div>
						<ul className="space-y-1">
							{trace.map((entry, index) => (
								<li key={index} className="text-xs text-muted-foreground">
									<span className="font-medium text-foreground">{(entry.args.terms ?? []).join(entry.args.match === "all" ? " + " : " / ")}</span>
									{entry.args.why ? ` — ${entry.args.why}` : ""}
									{` (${t("aiTopMatches.brief.hits", { count: entry.count })})`}
								</li>
							))}
						</ul>
						{stats?.scout_notes && <p className="mt-1.5 text-xs italic text-muted-foreground">{stats.scout_notes}</p>}
					</div>
				)}
			</CollapsibleContent>
		</Collapsible>
	);
}
